import { AwaitContainer, Button, Card, CardContent, CardHeader, Tabs, TabsContent } from '@ibsys/kit';
import { ArrowLeft } from 'lucide-react';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectedDialogDataTab, useSettings } from '@/entities/settings';
import { cn } from '@/shared/lib';
import { MessageMarkTargetsContext, useDialogDataDerived, useMessageMarkTargets } from '../model';
import { AttachmentsTab } from './AttachmentsTab';
import { CommentsTab } from './CommentsTab';
import { CopyDialogLinkButton } from './CopyDialogLinkButton';
import { DialogDataTab } from './DialogDataTab';
import { DialogDataTabNav } from './DialogDataTabNav';
import { DialogDataTabsSettings } from './DialogDataTabsSettings';
import { ExpertReportTab } from './ExpertReportTab';
import { RawDataTab } from './RawDataTab';

/** Идентификаторы фиксированных табов секции данных диалога. */
const TAB_ATTRIBUTES = 'attributes';
const TAB_COMMENTS = 'comments';
const TAB_ATTACHMENTS = 'attachments';
const TAB_RAW_DATA = 'raw-data';

/** Префикс ключа таба экспертного отчёта. Полный ключ: `expert-report-<expertReportId>`. */
const EXPERT_REPORT_TAB_PREFIX = 'expert-report-';

/**
 * Пропсы секции данных диалога.
 *
 * Активная секция внутри таба «Атрибуты» (deep-link) управляется самим табом
 * через URL hash — сюда передавать ничего не нужно.
 */
type Props = {
  /** ID диалога, данные которого отображаются. */
  dialogId: number;
  className?: string;
  /** Скрыть кнопку «Назад» (например, когда секция встроена без навигации). */
  hideBackButton?: boolean;
  /** Обработчик нажатия «Назад». */
  onBack?: () => void;
  /** Активный таб (управляется снаружи через searchParams). */
  activeTab?: string;
  /** Вызывается при смене таба. */
  onTabChange?: (tab: string) => void;
};

export const DialogDataSection: FC<Props> = React.memo(
  ({ dialogId, className, hideBackButton = false, onBack, activeTab, onTabChange }) => {
    const { t } = useTranslation(['dialogs', 'common']);
    const { groupsWithValues, allAttributes, doneExpertReports, conversationData, isLoading } = useDialogDataDerived(
      String(dialogId),
    );
    const messageMarkTargets = useMessageMarkTargets(dialogId);
    const { dialogsPage, setDialogsSettings } = useSettings();
    const hiddenDataTabs = dialogsPage?.hiddenDataTabs ?? [];

    // Выбор пользователя: адресом либо запомненный за ним. Экрану нужен показанный таб, поэтому
    // у него есть умолчание: ничего не выбрано — таб атрибутов.
    const selectedTab = useSelectedDialogDataTab(activeTab);
    const currentTab = selectedTab || TAB_ATTRIBUTES;

    // Выбор таба переживает выход из диалога, поэтому хранится за пользователем, а не только
    // в адресе карточки. Секция, встроенная без навигации (обработчика смены нет), остаётся
    // неуправляемой: переключать в ней табы по-прежнему нельзя.
    const handleTabChange = useMemo(
      () =>
        onTabChange
          ? (tab: string) => {
              setDialogsSettings({ lastDataTab: tab });
              onTabChange(tab);
            }
          : undefined,
      [onTabChange, setDialogsSettings],
    );

    const allTabOptions = useMemo(
      () => [
        { key: TAB_ATTRIBUTES, title: t('dialogs:attributes') },
        ...doneExpertReports.map((er) => ({
          key: `${EXPERT_REPORT_TAB_PREFIX}${er.expertReportId}`,
          title: er.expertReportName,
        })),
        { key: TAB_COMMENTS, title: t('dialogs:comments') },
        { key: TAB_ATTACHMENTS, title: t('dialogs:attachments') },
        { key: TAB_RAW_DATA, title: t('dialogs:rawData') },
      ],
      [doneExpertReports, t],
    );

    const tabOptions = useMemo(
      () => allTabOptions.filter((opt) => !hiddenDataTabs.includes(opt.key)),
      [allTabOptions, hiddenDataTabs],
    );

    // Отображаемый таб. Желаемый таб (`currentTab`) может отсутствовать у текущей записи —
    // её отчёт не построен — или быть скрыт пользователем. Тогда показываем первый доступный
    // таб, НЕ переписывая сам выбор — ни в адресе, ни в настройке: перекликивая записи и
    // открывая диалоги подряд, пользователь не теряет выбор, он снова применяется к записям,
    // где такой таб есть.
    const effectiveTab = useMemo(
      () => (tabOptions.some((opt) => opt.key === currentTab) ? currentTab : (tabOptions[0]?.key ?? currentTab)),
      [tabOptions, currentTab],
    );

    // Ссылка на диалог задаёт ту вкладку, которую копирующий видит, — а видит он `effectiveTab`,
    // а не свой выбор: выбранной вкладки может не быть у этого диалога или она скрыта его
    // настройкой. Вкладку не выбирали вовсе — ссылка её не задаёт: навязывать получателю
    // умолчание копирующего нечем, у него действует свой порядок показа вкладок.
    const linkTab = selectedTab ? effectiveTab : undefined;

    return (
      <MessageMarkTargetsContext.Provider value={messageMarkTargets}>
        <div className={cn('flex h-full flex-col gap-6 w-full', className)}>
          <Card className="overflow-hidden gap-0 h-full p-0">
            <Tabs value={effectiveTab} onValueChange={handleTabChange} asChild>
              <AwaitContainer isLoading={isLoading} className="overflow-hidden flex flex-col flex-1">
                {!isLoading && (
                  <>
                    <CardHeader className="pt-3 relative z-20">
                      <div className="flex gap-3 items-center min-w-0">
                        {!hideBackButton && onBack && (
                          <Button variant="link" icon={<ArrowLeft />} onClick={onBack}>
                            {t('common:back')}
                          </Button>
                        )}
                        {/* Переключатель разделов делит строку с настройками: длинное название
                          раздела сокращается по ширине карточки, а не распирает её */}
                        <div className="flex-1 min-w-0">
                          <DialogDataTabNav options={tabOptions} value={effectiveTab} onValueChange={handleTabChange} />
                        </div>
                        <div className="ml-auto shrink-0 flex gap-1 items-center">
                          <CopyDialogLinkButton dialogId={dialogId} tab={linkTab} />
                          <DialogDataTabsSettings options={allTabOptions} />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="overflow-hidden flex flex-col flex-1">
                      <TabsContent value={TAB_ATTRIBUTES} className="overflow-hidden flex flex-col w-full">
                        <DialogDataTab
                          conversationId={+dialogId}
                          groupsWithValues={groupsWithValues}
                          allAttributes={allAttributes}
                          showMainFields
                        />
                      </TabsContent>
                      <TabsContent value={TAB_COMMENTS} className="flex-1 overflow-hidden">
                        <CommentsTab conversationId={+dialogId} />
                      </TabsContent>
                      <TabsContent value={TAB_ATTACHMENTS} className="flex-1 overflow-hidden flex flex-col">
                        <AttachmentsTab conversationId={+dialogId} />
                      </TabsContent>
                      <TabsContent value={TAB_RAW_DATA} className="flex-1 overflow-hidden">
                        <RawDataTab data={conversationData?.externalRawData} />
                      </TabsContent>
                      {doneExpertReports.map((er) => (
                        <TabsContent
                          key={er.id}
                          value={`${EXPERT_REPORT_TAB_PREFIX}${er.expertReportId}`}
                          className="overflow-hidden"
                        >
                          <ExpertReportTab conversationId={+dialogId} expertReportId={er.expertReportId} />
                        </TabsContent>
                      ))}
                    </CardContent>
                  </>
                )}
              </AwaitContainer>
            </Tabs>
          </Card>
        </div>
      </MessageMarkTargetsContext.Provider>
    );
  },
);
